import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Play, Youtube, Music2, ArrowRight, Headphones, Instagram, Mic, Flame } from 'lucide-react';
import EpisodeCard from '@/components/EpisodeCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a027839a-8849-4776-a8ba-c961dd10b69f/artifacts/co2pjy4b_IMG_20260323_062123.jpg";
const YOUTUBE_URL = "https://youtube.com/@letsgetintoitfbe?si=lyzA8U32o3csvxTK";
const SPOTIFY_URL = "https://open.spotify.com/show/6B1NRfylqvaSDTedOPD3iz?si=RGWDLBCTQgOY7Hoh1b-mCg";
const INSTAGRAM_URL = "https://www.instagram.com/letsgetintoitfbe";
const TIKTOK_URL = "https://www.tiktok.com/@letsgetintoitfbe";

const TikTokIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z"/>
  </svg>
);

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function HomePage() {
  const [latestEpisode, setLatestEpisode] = useState(null);
  const [recentEpisodes, setRecentEpisodes] = useState([]);
  const [igClips, setIgClips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [latestRes, episodesRes, clipsRes] = await Promise.all([
          axios.get(`${API}/episodes/latest`),
          axios.get(`${API}/episodes`),
          axios.get(`${API}/instagram-clips`)
        ]);
        setLatestEpisode(latestRes.data);
        const all = episodesRes.data || [];
        setRecentEpisodes(all.slice(1, 4));
        setIgClips(clipsRes.data || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const extractYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/]+)/);
    return match ? match[1] : null;
  };

  const extractSpotifyId = (url) => {
    if (!url) return null;
    const match = url.match(/spotify\.com\/episode\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  return (
    <main data-testid="home-page" className="pt-16 md:pt-20 pb-20">
      {/* ===== HERO ===== */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_right,rgba(255,42,42,0.12),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(255,42,42,0.08),transparent_50%)]" />
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#FF2A2A]/[0.06] rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: headline + sub (mobile: headline first) */}
            <motion.div
              initial="hidden" animate="visible" variants={stagger}
              className="space-y-8"
            >
              <motion.div variants={fadeUp} transition={{ duration: 0.6 }}>
                <div className="inline-flex items-center gap-2 bg-[#FF2A2A]/10 border border-[#FF2A2A]/20 rounded-full px-4 py-1.5 mb-6">
                  <Flame size={14} className="text-[#FF2A2A]" />
                  <span className="font-body text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">Now Streaming</span>
                </div>
                <h1 className="font-heading text-6xl sm:text-7xl lg:text-[5.5rem] leading-[0.9] uppercase">
                  LET'S GET
                  <br />
                  <span className="text-gradient">IN TO IT</span>
                </h1>
                <p className="font-body text-base md:text-lg text-[#A1A1AA] mt-5 max-w-md leading-relaxed">
                  Comedy about growing up and getting older. Real conversations, unfiltered opinions, and everything in between.
                </p>
              </motion.div>

              {/* Mobile poster (shows between headline and CTAs on mobile, hidden on desktop) */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="flex justify-center lg:hidden"
              >
                <div className="relative">
                  <div className="absolute -inset-8 bg-[#FF2A2A]/[0.12] blur-[60px] rounded-full" />
                  <div className="relative w-72 h-72 sm:w-80 sm:h-80" style={{ aspectRatio: '1/1' }}>
                    <img
                      src={LOGO_URL}
                      alt="Let's Get In To It Podcast"
                      fetchpriority="high"
                      className="w-full h-full object-cover rounded-2xl border-2 border-[#FF2A2A]/20 shadow-2xl shadow-[#FF2A2A]/10"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} transition={{ duration: 0.6 }} className="flex flex-wrap gap-3">
                <a
                  href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="hero-youtube-btn"
                  className="inline-flex items-center gap-2.5 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-7 py-3.5 rounded-lg hover:bg-[#E60000] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#FF2A2A]/20"
                >
                  <Youtube size={18} /> Watch on YouTube
                </a>
                <a
                  href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="hero-spotify-btn"
                  className="inline-flex items-center gap-2.5 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-7 py-3.5 rounded-lg hover:bg-[#CC0000] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#FF2A2A]/20"
                >
                  <Music2 size={18} /> Listen on Spotify
                </a>
              </motion.div>
            </motion.div>

            {/* Right: Logo + glow (desktop only) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:flex justify-end"
            >
              <div className="relative">
                <div className="absolute -inset-10 bg-[#FF2A2A]/[0.12] blur-[80px] rounded-full" />
                <div className="absolute -inset-6 bg-[#FF2A2A]/[0.06] blur-[40px] rounded-full" />
                <div className="relative w-[460px] h-[460px]" style={{ aspectRatio: '1/1' }}>
                  <img
                    src={LOGO_URL}
                    alt="Let's Get In To It Podcast"
                    data-testid="hero-logo"
                    fetchpriority="high"
                    className="w-full h-full object-cover rounded-2xl border-2 border-[#FF2A2A]/20 shadow-2xl shadow-[#FF2A2A]/10"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <div className="py-4 overflow-hidden">
        <div className="section-divider mb-4" />
        <Marquee speed={35} gradient={false}>
          <span className="marquee-text mx-6">LET'S GET IN TO IT</span>
          <span className="marquee-text-filled mx-6">REAL TALK</span>
          <span className="marquee-text mx-6">NO FILTER</span>
          <span className="marquee-text-filled mx-6">COMEDY PODCAST</span>
          <span className="marquee-text mx-6">UNFILTERED</span>
          <span className="marquee-text-filled mx-6">PHILLY VIBES</span>
        </Marquee>
        <div className="section-divider mt-4" />
      </div>

      {/* ===== LATEST EPISODE ===== */}
      {latestEpisode && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="latest-episode-section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#FF2A2A] animate-pulse" />
                <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A]">NEWEST DROP</span>
              </div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-8">
                {latestEpisode.title}
              </h2>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-8"
            >
              {latestEpisode.video_url ? (
                <div className="aspect-video bg-[#141414] rounded-xl border border-white/5 overflow-hidden glow-red">
                  <video
                    data-testid="latest-episode-uploaded-video"
                    src={`${process.env.REACT_APP_BACKEND_URL}${latestEpisode.video_url}`}
                    controls className="w-full h-full" poster={latestEpisode.thumbnail_url || undefined}
                  />
                </div>
              ) : extractYoutubeId(latestEpisode.youtube_url) ? (
                <div className="aspect-video bg-[#141414] rounded-xl border border-white/5 overflow-hidden glow-red">
                  <iframe
                    data-testid="latest-episode-video"
                    src={`https://www.youtube.com/embed/${extractYoutubeId(latestEpisode.youtube_url)}`}
                    title={latestEpisode.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen className="w-full h-full"
                  />
                </div>
              ) : extractSpotifyId(latestEpisode.spotify_url) ? (
                <div className="bg-[#141414] rounded-xl border border-white/5 p-4 glow-red" data-testid="latest-spotify-embed">
                  <iframe
                    src={`https://open.spotify.com/embed/episode/${extractSpotifyId(latestEpisode.spotify_url)}?utm_source=generator&theme=0`}
                    width="100%" height="352" frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy" title={`${latestEpisode.title} - Spotify`}
                  />
                </div>
              ) : (
                <div className="aspect-video bg-[#141414] rounded-xl border border-white/5 flex items-center justify-center">
                  <Headphones size={64} className="text-[#FF2A2A]/30" />
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-4 space-y-4"
            >
              <div className="bg-[#141414] rounded-xl border border-white/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-[#FF2A2A] px-3 py-1 rounded-md">
                    <span className="font-body text-xs font-bold text-white uppercase tracking-wider">
                      EP {latestEpisode.episode_number}
                    </span>
                  </div>
                  <span className="font-body text-xs text-[#FF2A2A] font-semibold">Latest</span>
                </div>
                <p className="font-body text-sm text-[#A1A1AA] leading-relaxed">
                  {latestEpisode.description || "Tune in for another fire episode!"}
                </p>
              </div>

              <div className="space-y-3">
                {latestEpisode.youtube_url && (
                  <a href={latestEpisode.youtube_url} target="_blank" rel="noopener noreferrer"
                    data-testid="latest-youtube-link"
                    className="flex items-center gap-3 bg-[#141414] rounded-xl border border-white/5 hover:border-[#FF2A2A]/40 p-4 transition-all group card-lift"
                  >
                    <Youtube size={20} className="text-[#FF2A2A]" />
                    <span className="font-body text-sm font-semibold">Watch on YouTube</span>
                    <ArrowRight size={16} className="ml-auto opacity-0 group-hover:opacity-100 text-[#FF2A2A] transition-opacity" />
                  </a>
                )}
                {latestEpisode.spotify_url && (
                  <a href={latestEpisode.spotify_url} target="_blank" rel="noopener noreferrer"
                    data-testid="latest-spotify-link"
                    className="flex items-center gap-3 bg-[#141414] rounded-xl border border-white/5 hover:border-[#FF2A2A]/40 p-4 transition-all group card-lift"
                  >
                    <Music2 size={20} className="text-[#FF2A2A]" />
                    <span className="font-body text-sm font-semibold">Listen on Spotify</span>
                    <ArrowRight size={16} className="ml-auto opacity-0 group-hover:opacity-100 text-[#FF2A2A] transition-opacity" />
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== NO EPISODES ===== */}
      {!loading && !latestEpisode && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 text-center" data-testid="no-episodes-section">
          <Headphones size={64} className="mx-auto text-[#FF2A2A]/30 mb-6" />
          <h2 className="font-heading text-3xl uppercase mb-3">COMING SOON</h2>
          <p className="font-body text-[#A1A1AA]">Episodes dropping soon. Stay tuned!</p>
          <div className="flex justify-center gap-4 mt-8">
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="no-ep-youtube"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-6 py-3 rounded-lg hover:bg-[#E60000] transition-all">
              <Youtube size={16} /> Subscribe
            </a>
            <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="no-ep-spotify"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-6 py-3 rounded-lg hover:bg-[#CC0000] transition-all">
              <Music2 size={16} /> Follow
            </a>
          </div>
        </section>
      )}

      {/* ===== RECENT EPISODES ===== */}
      {recentEpisodes.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24" data-testid="recent-episodes-section">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-end justify-between mb-10">
              <div>
                <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-3 block">MORE FIRE</span>
                <h2 className="font-heading text-4xl sm:text-5xl uppercase">CATCH UP</h2>
              </div>
              <Link to="/archive" data-testid="view-all-episodes-btn"
                className="hidden sm:flex items-center gap-2 font-body text-sm font-bold text-[#FF2A2A] hover:underline uppercase tracking-wide">
                VIEW ALL <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentEpisodes.map((ep, idx) => (
              <EpisodeCard key={ep.id} episode={ep} index={idx} />
            ))}
          </div>

          <Link to="/archive" data-testid="view-all-mobile-btn"
            className="sm:hidden flex items-center justify-center gap-2 mt-8 font-body text-sm font-bold text-[#FF2A2A] uppercase tracking-wide">
            VIEW ALL EPISODES <ArrowRight size={16} />
          </Link>
        </section>
      )}

      {/* ===== MEET THE HOSTS ===== */}
      <section className="py-16 md:py-24" data-testid="hosts-section">
        <div className="section-divider mb-16" />
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] block mb-3">YOUR HOSTS</span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase">MEET THE CREW</h2>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: "TONE",
                handle: "@tonemeezy",
                ig: "https://www.instagram.com/tonemeezy",
                bio: "The voice of reason... until he's not. Tone brings the calm energy that somehow always ends up in chaos. Philly raised, strong opinions, zero filter.",
                gradient: "from-[#FF2A2A]/20 to-[#FF2A2A]/5",
                photo: "https://customer-assets-agu9un31.emergentagent.net/job_getintoit-radio/artifacts/vptcqwd4_IMG_20260925_180852.webp"
              },
              {
                name: "SHIZZ",
                handle: "@shizzygetbizzy",
                ig: "https://www.instagram.com/shizzygetbizzy",
                bio: "If there's a hot take, Shizz already said it twice. The wildcard of the duo who keeps every episode unpredictable. You never know what's coming next.",
                gradient: "from-[#FF2A2A]/20 to-[#FF2A2A]/5",
                photo: "https://customer-assets-agu9un31.emergentagent.net/job_getintoit-radio/artifacts/yiu3pg4r_IMG_20250924_103355.png"
              }
            ].map((host, idx) => (
              <motion.div
                key={host.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                data-testid={`host-card-${host.name.toLowerCase()}`}
                className="bg-[#141414] rounded-xl border border-white/5 p-8 card-lift group"
              >
                <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#FF2A2A]/30 mx-auto mb-6">
                  <img src={host.photo} alt={host.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-heading text-3xl text-center uppercase mb-1">{host.name}</h3>
                <p className="font-body text-xs text-center text-[#71717A] mb-4">{host.handle}</p>
                <p className="font-body text-sm text-[#A1A1AA] text-center leading-relaxed mb-6">{host.bio}</p>
                <a
                  href={host.ig} target="_blank" rel="noopener noreferrer"
                  data-testid={`host-ig-${host.name.toLowerCase()}`}
                  className="flex items-center justify-center gap-2 bg-white/5 rounded-lg py-2.5 hover:bg-[#E1306C]/10 hover:text-[#E1306C] transition-colors font-body text-sm font-semibold"
                >
                  <Instagram size={16} /> Follow {host.handle}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BEST MOMENTS (IG CLIPS) ===== */}
      {igClips.length > 0 && (
        <section className="py-16 md:py-24" data-testid="instagram-highlights-section">
          <div className="section-divider mb-16" />
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="flex items-end justify-between mb-10">
                <div>
                  <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#E1306C] block mb-3">BEST MOMENTS</span>
                  <h2 className="font-heading text-4xl sm:text-5xl uppercase">FROM THE GRAM</h2>
                </div>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="ig-highlights-follow-btn"
                  className="hidden sm:flex items-center gap-2 font-body text-sm font-bold text-[#E1306C] hover:underline uppercase tracking-wide">
                  <Instagram size={16} /> FOLLOW US <ArrowRight size={16} />
                </a>
              </motion.div>
            </motion.div>

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${igClips.length >= 4 ? 'lg:grid-cols-4' : igClips.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4`}>
              {igClips.map((clip, idx) => (
                <motion.div
                  key={clip.id || clip.reel_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  data-testid={`ig-reel-${clip.reel_id}`}
                  className="bg-[#141414] rounded-xl border border-white/5 hover:border-[#E1306C]/40 transition-colors duration-300 overflow-hidden"
                >
                  <iframe
                    src={`https://www.instagram.com/reel/${clip.reel_id}/embed/`}
                    width="100%" height="480" frameBorder="0" scrolling="no"
                    allowTransparency="true" allow="encrypted-media"
                    title={`Instagram Reel ${idx + 1}`} className="w-full"
                  />
                </motion.div>
              ))}
            </div>

            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
              data-testid="ig-highlights-follow-mobile"
              className="sm:hidden flex items-center justify-center gap-2 mt-8 font-body text-sm font-bold text-[#E1306C] uppercase tracking-wide">
              <Instagram size={16} /> FOLLOW @LETSGETINTOITFBE <ArrowRight size={16} />
            </a>
          </div>
        </section>
      )}

      {/* ===== SOCIALS CTA ===== */}
      <section className="py-16 md:py-24" data-testid="socials-section">
        <div className="section-divider mb-16" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] block mb-4">CONNECT WITH US</span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-10">FOLLOW THE PODCAST</h2>
            </motion.div>
          </motion.div>

          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-5">
            {[
              { url: YOUTUBE_URL, icon: <Youtube size={28} />, label: "YouTube", sub: "@LetsGetInToItFBE", color: "#FF2A2A", testId: "social-youtube-card" },
              { url: SPOTIFY_URL, icon: <Music2 size={28} />, label: "Spotify", sub: "Listen & Follow", color: "#1DB954", testId: "social-spotify-card" },
              { url: INSTAGRAM_URL, icon: <Instagram size={28} />, label: "Instagram", sub: "@LetsGetInToItFBE", color: "#E1306C", testId: "social-instagram-card" },
              { url: TIKTOK_URL, icon: <TikTokIcon size={28} />, label: "TikTok", sub: "@LetsGetInToItFBE", color: "#00f2ea", testId: "social-tiktok-card" },
            ].map((s) => (
              <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
                data-testid={s.testId}
                className="group flex items-center gap-4 bg-[#141414] rounded-xl border border-white/5 hover:border-opacity-50 p-6 sm:p-8 transition-all duration-300 card-lift"
                style={{ '--hover-border': s.color }}
              >
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: `${s.color}15` }}>
                  <span style={{ color: s.color }}>{s.icon}</span>
                </div>
                <div className="text-left">
                  <p className="font-heading text-xl uppercase">{s.label}</p>
                  <p className="font-body text-xs text-[#71717A]">{s.sub}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
